/* AdminSubcategories - Manage product subcategories per main category
 * Updated: 2026-08-03 - Added thumbnail image upload (reuses product-images bucket)
 * Features: Create, edit, delete, reorder subcategories; link to parent categories; thumbnail upload
 */
import Breadcrumb from '@/components/admin/Breadcrumb';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader as Loader2, Plus, Trash2, Pencil, X, ArrowUp, ArrowDown, Tag, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getProductImageUrl } from '@/lib/image-url';

interface Subcategory {
  id: string;
  name: string;
  slug: string;
  category_id: string;
  display_order: number;
  active: boolean;
  thumbnail_path: string | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

export default function AdminSubcategories() {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category_id: '',
    display_order: 0,
    active: true,
    thumbnail_path: '' as string,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchSubcategories(selectedCategoryId);
    } else {
      setSubcategories([]);
    }
  }, [selectedCategoryId]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setCategories(data || []);
      if (data && data.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Fout bij het ophalen van categorieën');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('product_subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .order('display_order');

      if (error) throw error;
      setSubcategories(data || []);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      toast.error('Fout bij het ophalen van subcategorieën');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingThumbnail(true);
      const file = files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `subcategories/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      setFormData(prev => ({ ...prev, thumbnail_path: filePath }));
      toast.success('Thumbnail succesvol geüpload');
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Fout bij het uploaden van thumbnail');
    } finally {
      setUploadingThumbnail(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveThumbnail = () => {
    setFormData(prev => ({ ...prev, thumbnail_path: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const catId = formData.category_id || selectedCategoryId;
    if (!catId) {
      toast.error('Selecteer eerst een hoofdcategorie');
      return;
    }

    const slug = formData.slug || generateSlug(formData.name);

    try {
      if (editingId) {
        const { error } = await supabase
          .from('product_subcategories')
          .update({
            name: formData.name,
            slug,
            category_id: catId,
            display_order: formData.display_order,
            active: formData.active,
            thumbnail_path: formData.thumbnail_path || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingId);

        if (error) throw error;
        toast.success('Subcategorie bijgewerkt');
      } else {
        const { error } = await supabase
          .from('product_subcategories')
          .insert({
            name: formData.name,
            slug,
            category_id: catId,
            display_order: formData.display_order,
            active: formData.active,
            thumbnail_path: formData.thumbnail_path || null,
          });

        if (error) throw error;
        toast.success('Subcategorie aangemaakt');
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', slug: '', category_id: '', display_order: 0, active: true, thumbnail_path: '' });
      fetchSubcategories(catId);
    } catch (err: any) {
      console.error('Error saving subcategory:', err);
      toast.error(err.message || 'Fout bij opslaan');
    }
  };

  const handleEdit = (sub: Subcategory) => {
    setEditingId(sub.id);
    setFormData({
      name: sub.name,
      slug: sub.slug,
      category_id: sub.category_id,
      display_order: sub.display_order,
      active: sub.active,
      thumbnail_path: sub.thumbnail_path || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (sub: Subcategory) => {
    if (!confirm(`Weet u zeker dat u "${sub.name}" wilt verwijderen? Producten in deze subcategorie krijgen geen subcategorie meer.`)) return;

    try {
      if (sub.thumbnail_path) {
        await supabase.storage.from('product-images').remove([sub.thumbnail_path]);
      }

      await supabase
        .from('products')
        .update({ subcategory_id: null })
        .eq('subcategory_id', sub.id);

      const { error } = await supabase
        .from('product_subcategories')
        .delete()
        .eq('id', sub.id);

      if (error) throw error;
      toast.success('Subcategorie verwijderd');
      fetchSubcategories(selectedCategoryId);
    } catch (err: any) {
      console.error('Error deleting subcategory:', err);
      toast.error(err.message || 'Fout bij verwijderen');
    }
  };

  const handleReorder = async (sub: Subcategory, direction: 'up' | 'down') => {
    const sorted = [...subcategories].sort((a, b) => a.display_order - b.display_order);
    const index = sorted.findIndex(s => s.id === sub.id);
    if (index < 0) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const swapSub = sorted[swapIndex];

    try {
      await Promise.all([
        supabase.from('product_subcategories').update({ display_order: swapSub.display_order }).eq('id', sub.id),
        supabase.from('product_subcategories').update({ display_order: sub.display_order }).eq('id', swapSub.id),
      ]);
      fetchSubcategories(selectedCategoryId);
    } catch (err) {
      console.error('Error reordering:', err);
      toast.error('Fout bij herordenen');
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      name: '',
      slug: '',
      category_id: selectedCategoryId || '',
      display_order: subcategories.length + 1,
      active: true,
      thumbnail_path: '',
    });
    setShowForm(true);
  };

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <Breadcrumb pageName="Subcategorieën" />
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-jv-primary flex items-center gap-2">
            <Tag className="w-6 h-6 text-jv-primary" />
            Subcategorieën
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Beheer subcategorieën per hoofdcategorie (bijv. Laurier onder Haagplanten)
          </p>
        </div>
        <Button onClick={handleAddNew} className="bg-jv-primary hover:bg-jv-dark text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe subcategorie
        </Button>
      </div>

      {/* Category selector */}
      <Card className="p-4">
        <Label className="block mb-2">Hoofdcategorie</Label>
        <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
          <SelectTrigger className="max-w-xs">
            <SelectValue placeholder="Selecteer een hoofdcategorie" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {/* Form */}
      {showForm && (
        <Card className="p-6 border-2 border-jv-primary/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-jv-dark">
              {editingId ? 'Subcategorie bewerken' : 'Nieuwe subcategorie'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowForm(false); setEditingId(null); }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-name">Naam</Label>
                <Input
                  id="sub-name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: editingId ? formData.slug : generateSlug(e.target.value),
                    });
                  }}
                  placeholder="Bijv. Laurier"
                  required
                />
              </div>
              <div>
                <Label htmlFor="sub-slug">Slug (URL)</Label>
                <Input
                  id="sub-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="bijv-laurier"
                />
              </div>
            </div>

            {/* Thumbnail upload */}
            <div>
              <Label>Thumbnail afbeelding</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border-2 border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50 flex-shrink-0">
                  {formData.thumbnail_path ? (
                    <img
                      src={getProductImageUrl(formData.thumbnail_path)}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingThumbnail}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingThumbnail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {formData.thumbnail_path ? 'Vervangen' : 'Uploaden'}
                  </Button>
                  {formData.thumbnail_path && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveThumbnail}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Verwijderen
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-order">Volgorde</Label>
                <Input
                  id="sub-order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="sub-active">Actief</Label>
                <Select
                  value={formData.active ? 'true' : 'false'}
                  onValueChange={(v) => setFormData({ ...formData, active: v === 'true' })}
                >
                  <SelectTrigger id="sub-active">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Ja</SelectItem>
                    <SelectItem value="false">Nee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="bg-jv-primary hover:bg-jv-dark text-white">
                {editingId ? 'Opslaan' : 'Aanmaken'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>
                Annuleren
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Subcategory list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-jv-primary" />
        </div>
      ) : subcategories.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500">
            {selectedCategory ? `Geen subcategorieën voor "${selectedCategory.name}"` : 'Selecteer een hoofdcategorie'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {subcategories.sort((a, b) => a.display_order - b.display_order).map((sub, index) => (
            <Card key={sub.id} className="p-4 flex items-center justify-between hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => handleReorder(sub, 'up')}
                    disabled={index === 0}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => handleReorder(sub, 'down')}
                    disabled={index === subcategories.length - 1}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                </div>
                <div className="w-12 h-12 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50 flex-shrink-0">
                  {sub.thumbnail_path ? (
                    <img
                      src={getProductImageUrl(sub.thumbnail_path)}
                      alt={sub.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-gray-300" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-jv-dark">{sub.name}</p>
                  <p className="text-xs text-gray-500">/{sub.slug} · volgorde: {sub.display_order}</p>
                </div>
                {!sub.active && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Inactief</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(sub)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(sub)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}